import { DEFAULT_DATE_FORMAT } from '@almaris/shared/utils/dates'

import { parseDate } from '@almaris/shared/utils/dates'
import {
  Application_Status_Enum,
  WebappSimpleEstateFragment,
  ApplicationIdAndStatusFragment,
} from '@gql'
import {
  isAuthenticActSigned,
  isPromiseOfSaleSigned,
} from '@almaris/shared/helpers/getApplicationInfo'

export type EstateWithApplicationsStatus = Omit<
  WebappSimpleEstateFragment,
  'features' | 'estate_sellers' | 'estate_medias' | 'seller' | 'sellerId'
> & {
  applications: ApplicationIdAndStatusFragment[]
}

export enum EstateStatus {
  /** Procedure en cours */
  InEdition = 'inEdition',
  MandateSigned = 'mandateSigned',
  AOInProgress = 'aoInProgress',
  AODone = 'aoDone',
  Archived = 'archived',
  InNegociation = 'inNegociation',
  UnderPromess = 'underPromess',
  Sold = 'sold',
}

// TODO: There is something missing here: as the first round of an estate is created when the estate is created, it may end before the estate is even published.
// In general, what we miss is a clear "in preparation" status or a clear indicator (boolean) that the estate was once published (which is not provided by `isPublished` or `isSaleOpened`)
// Pages like EstateSalePage use a combination of `isSaleOpen` and the presence of applications to know if the sale is open or not. But that's not enough.

/**
 * TODO move into hasura ?
 * - inEdition - En cours de complétion : au moment de la création du bien jusqu'à la signature du mandat
 * - mandateSigned - Mandat signé : le mandat est chargé et la date de démarrage de la vente n'est pas encore atteint
 * - aoInProgress - Appel d’offre en cours : Le mandat est chargé et la date de démarrage de la vente est lancé
 * - aoDone - Appel d'offre terminé : la date de fin de la vente est échue
 * - archived - archivé
 * @param estate
 * @returns
 */
export const getEstateStatus = (
  estate: EstateWithApplicationsStatus
): EstateStatus => {
  const { currentRoundId, mandate, archived } = estate
  const currentRound = estate.estateRounds.find(
    (round) => round.id === currentRoundId
  )

  if (!currentRound) return EstateStatus.InEdition

  const now = new Date()
  const hasMandate = !!mandate?.mandate_media?.length
  const { startDate, endDate } = currentRound
  const parsedStartDate = parseDate(startDate, DEFAULT_DATE_FORMAT) ?? now
  const parsedEndDate = parseDate(endDate, DEFAULT_DATE_FORMAT) ?? now
  const isOnSale = estate.isSaleOpen

  const acceptedApplication = estate.applications.find(
    (a) => a.status === Application_Status_Enum.Accepted
  )

  if (archived) return EstateStatus.Archived

  if (acceptedApplication) {
    const sold =
      acceptedApplication && isAuthenticActSigned(acceptedApplication)

    if (sold) return EstateStatus.Sold

    const unUnderPromess =
      !sold &&
      acceptedApplication &&
      isPromiseOfSaleSigned(acceptedApplication) &&
      !isAuthenticActSigned(acceptedApplication)

    if (unUnderPromess) return EstateStatus.UnderPromess

    return EstateStatus.InNegociation
  } else {
    // If no application has been accepted, we simply consider the sale as done
    if (now.getTime() > parsedEndDate.getTime()) {
      return EstateStatus.AODone
    }

    // TODO: Should be
    // if (hasMandate && isOnSale && now.getTime() > parsedStartDate.getTime())
    // But we disabled the mandatory mandate for now
    if (isOnSale && now.getTime() > parsedStartDate.getTime()) {
      return EstateStatus.AOInProgress
    }

    return hasMandate ? EstateStatus.MandateSigned : EstateStatus.InEdition
  }
}

export const getEstateStatusBasedLink = (
  estate: EstateWithApplicationsStatus
) => {
  const status = getEstateStatus(estate)
  let link = `estates/${estate.id}`

  switch (status) {
    case EstateStatus.InEdition:
      link = `estates/${estate.id}`
      break
    case EstateStatus.MandateSigned:
      link = `estates/${estate.id}/mandate`
      break
    case EstateStatus.AOInProgress:
    case EstateStatus.AODone:
    case EstateStatus.Sold:
    case EstateStatus.InNegociation:
    case EstateStatus.UnderPromess:
    case EstateStatus.Archived:
      link = `estates/${estate.id}/sale`
      break
  }
  return link
}
